import React from 'react'
import {createPortal} from 'react-dom'
import './Tooltip.css'

const MAX_CHARS = 60
const EDGE_GAP = 8
const ANCHOR_GAP = 6

let nextId = 0

export const Tooltip = ({
  description,
  variant = 'top',
  block = false,
  className = '',
  suppressNativeTitle = false,
  children,
}) => {
  const triggerRef = React.useRef(null)
  const bubbleRef = React.useRef(null)
  const [id] = React.useState(() => `app-tooltip-${nextId++}`)
  const [open, setOpen] = React.useState(false)
  const [style, setStyle] = React.useState({})

  // The bubble lives in document.body, so it is never clipped by an ancestor.
  // Place it against the trigger's viewport rect: centred, shrunk to fit the
  // viewport (text wraps), clamped sideways, and flipped to the other side
  // when the requested one has no room.
  const position = React.useCallback(() => {
    const trigger = triggerRef.current
    const bubble = bubbleRef.current
    if (!trigger || !bubble) return
    const rect = trigger.getBoundingClientRect()
    // At most MAX_CHARS characters per line, and never wider than the viewport.
    const maxWidth = `min(${MAX_CHARS}ch, ${window.innerWidth - EDGE_GAP * 2}px)`
    bubble.style.maxWidth = maxWidth
    const {offsetWidth, offsetHeight} = bubble

    const left = Math.max(
      EDGE_GAP,
      Math.min(
        rect.left + rect.width / 2 - offsetWidth / 2,
        window.innerWidth - EDGE_GAP - offsetWidth,
      ),
    )
    const roomAbove = rect.top - ANCHOR_GAP - EDGE_GAP
    const roomBelow = window.innerHeight - rect.bottom - ANCHOR_GAP - EDGE_GAP
    const placeTop =
      variant === 'top'
        ? roomAbove >= offsetHeight || roomAbove >= roomBelow
        : roomBelow < offsetHeight && roomAbove > roomBelow
    const top = placeTop
      ? rect.top - ANCHOR_GAP - offsetHeight
      : rect.bottom + ANCHOR_GAP

    setStyle({maxWidth, left, top})
  }, [variant])

  React.useLayoutEffect(() => {
    if (!open) return undefined
    position()
    window.addEventListener('scroll', position, true)
    window.addEventListener('resize', position)
    return () => {
      window.removeEventListener('scroll', position, true)
      window.removeEventListener('resize', position)
    }
  }, [open, position])

  // Third-party iframes (e.g. Recurly fields) carry their own `title`, which
  // makes the browser show a native tooltip over ours. Move it to aria-label so
  // screen readers keep the name, and drop the title. Iframes can be injected
  // after mount, so watch for them.
  React.useEffect(() => {
    const trigger = triggerRef.current
    if (!suppressNativeTitle || !trigger) return undefined
    const strip = () => {
      trigger.querySelectorAll('[title]').forEach(el => {
        if (!el.getAttribute('aria-label')) {
          el.setAttribute('aria-label', el.getAttribute('title'))
        }
        el.removeAttribute('title')
      })
    }
    strip()
    const observer = new MutationObserver(strip)
    observer.observe(trigger, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['title'],
    })
    return () => observer.disconnect()
  }, [suppressNativeTitle])

  const show = () => setOpen(true)
  const hide = () => setOpen(false)

  return (
    <>
      <span
        ref={triggerRef}
        className={['app-tooltip', block && 'app-tooltip--block', className]
          .filter(Boolean)
          .join(' ')}
        aria-describedby={open ? id : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <span
            ref={bubbleRef}
            id={id}
            role="tooltip"
            className="app-tooltip-bubble"
            style={style}
          >
            {description}
          </span>,
          document.body,
        )}
    </>
  )
}

export default Tooltip
